import { Controller, useFormContext } from 'react-hook-form';
import { HeightWeightQuestionProps } from '../../types';
import { ScrollComponent } from '../components/scroll-component';

const DEFAULT_HEIGHT = 5.5;
const DEFAULT_WEIGHT = 130;

const getDefaultValue = (
  questionType: 'height' | 'weight' | 'height-weight',
): number | { height: number; weight: number } => {
  switch (questionType) {
    case 'height':
      return DEFAULT_HEIGHT;
    case 'weight':
      return DEFAULT_WEIGHT;
    case 'height-weight':
      return {
        height: DEFAULT_HEIGHT,
        weight: DEFAULT_WEIGHT,
      };
  }
};

export function HeightWeightQuestion({ question }: HeightWeightQuestionProps) {
  const { control, clearErrors } = useFormContext();

  return (
    <Controller
      control={control}
      name={question.slug}
      defaultValue={getDefaultValue(question.questionType)}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // Handle the special case of height_weight (not in generated types yet)
        const questionType = question.questionType;

        switch (questionType) {
          case 'height': {
            // Handle height-only question
            const heightValue = Number(value);

            const handleHeightChange = (newHeight: number) => {
              onChange(newHeight);
              clearErrors(question.slug);
            };

            return (
              <div className="questionnaire-question-content">
                <ScrollComponent value={heightValue} onChange={handleHeightChange} type="height" unit="ft" />
                {error && error.message && (
                  <div className="w-full">
                    <p className="input-error-message">{error.message}</p>
                  </div>
                )}
              </div>
            );
          }

          case 'weight': {
            // Handle weight-only question
            const weightValue = Number(value);

            const handleWeightChange = (newWeight: number) => {
              onChange(newWeight);
              clearErrors(question.slug);
            };

            return (
              <div className="questionnaire-question-content">
                <ScrollComponent value={weightValue} onChange={handleWeightChange} type="weight" unit="lb" />
                {error && error.message && (
                  <div className="w-full">
                    <p className="input-error-message">{error.message}</p>
                  </div>
                )}
              </div>
            );
          }

          case 'height-weight': {
            // Handle combined height-weight question
            // Handle the case where value might be undefined or not in expected format
            const currentValue = value || { height: DEFAULT_HEIGHT, weight: DEFAULT_WEIGHT };

            const handleWeightChange = (newWeight: number) => {
              onChange({
                ...currentValue,
                weight: newWeight.toString(),
              });
              // Clear any existing error for this field when user changes weight
              clearErrors(question.slug);
            };

            const handleHeightChange = (newHeight: number) => {
              onChange({
                ...currentValue,
                height: newHeight.toString(), // add units to string?
              });
              // Clear any existing error for this field when user changes height
              clearErrors(question.slug);
            };

            // Parse string values to numbers for the ScrollComponent
            const weightValue = Number(currentValue.weight);
            const heightValue = Number(currentValue.height);

            return (
              <div className="questionnaire-question-content">
                <ScrollComponent value={weightValue} onChange={handleWeightChange} type="weight" unit="lb" />
                <ScrollComponent value={heightValue} onChange={handleHeightChange} type="height" unit="ft" />
              </div>
            );
          }

          default: {
            console.warn(`Unknown question type in HeightWeightQuestion: ${questionType}`);
            return (
              <div className="questionnaire-question-content">
                <p className="input-error-message">Unknown question type: {questionType}</p>
              </div>
            );
          }
        }
      }}
    />
  );
}
